import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { PrefsProvider } from '@/lib/prefs';
import { FeatureProvider } from '@/lib/features';
import '@/App.css';
import '@/theme-dark.css';

import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import PendingPage from '@/pages/PendingPage';
import WrongDomainPage from '@/pages/WrongDomainPage';
import HomePage from '@/pages/HomePage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import QCHistoryPage from '@/pages/QCHistoryPage';
import AdminAccessPage from '@/pages/AdminAccessPage';
import WOReviewPage from '@/pages/WOReviewPage';
import WorkOrdersPage from '@/pages/WorkOrdersPage';
import WorkOrderUploadPage from '@/pages/WorkOrderUploadPage';
import WorkOrderVerifyPage from '@/pages/WorkOrderVerifyPage';
import KnowledgePage from '@/pages/KnowledgePage';
import KnowledgeHubPage from '@/pages/KnowledgeHubPage';
import TemplatesPage from '@/pages/TemplatesPage';
import EmailsPage from '@/pages/EmailsPage';
import WizardPage from '@/pages/WizardPage';
import NotificationsPage from '@/pages/NotificationsPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import AdminAuditPage from '@/pages/AdminAuditPage';
import SettingsPage from '@/pages/SettingsPage';
import AdminSettingsPage from '@/pages/AdminSettingsPage';
import PendingInvoicesPage from '@/pages/PendingInvoicesPage';
import WorkOrderManualPage from '@/pages/WorkOrderManualPage';
import QuoteRegisterPage from '@/pages/QuoteRegisterPage';
import VisitsPage from '@/pages/VisitsPage';
import ReleaseManagerPage from '@/pages/ReleaseManagerPage';
import ReportsPage from '@/pages/ReportsPage';
import ReportsKraPage from '@/pages/ReportsKraPage';
import MyWorkPage from '@/pages/MyWorkPage';
import DataQualityPage from '@/pages/DataQualityPage';
import DriveSetupWizardPage from '@/pages/DriveSetupWizardPage';
import KraDashboardPage from '@/pages/KraDashboardPage';
import TimesheetsPage from '@/pages/TimesheetsPage';
import CommitmentsPage from '@/pages/CommitmentsPage';
import MyWeekPage from '@/pages/MyWeekPage';
import AdminHolidayCalendarPage from '@/pages/AdminHolidayCalendarPage';
import AdminWorArchivePage from '@/pages/AdminWorArchivePage';
import AdminGateDelegationsPage from '@/pages/AdminGateDelegationsPage';
import AdminSopNeedsConfirmationPage from '@/pages/AdminSopNeedsConfirmationPage';
import MasterProjectsPage from '@/pages/MasterProjectsPage';
import TeamDirectoryPage from '@/pages/TeamDirectoryPage';
import MyDesignDeskPage from '@/pages/MyDesignDeskPage';
import GuidePage from '@/pages/GuidePage';
import AdminFeedbackPage from '@/pages/AdminFeedbackPage';
import ApprovalsPage from '@/pages/ApprovalsPage';
import FollowupsPage from '@/pages/FollowupsPage';
import ArchivesPage from '@/pages/ArchivesPage';
import OutboxPage from '@/pages/OutboxPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import VerifyOTPPage from '@/pages/VerifyOTPPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import { GuidedTourProvider } from '@/components/tour/GuidedTour';

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8 text-[hsl(var(--foreground-muted))]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (user.status === 'pending') return <Navigate to="/pending" replace />;
  if (user.status === 'disabled') return <Navigate to="/login" replace />;
  // Force post-admin-reset password change before anything else. Skip the guard
  // if the user is already on /change-password so they can actually reset it.
  if (user.force_password_change && loc.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  if (allow && !allow.includes(user.role)) return <Navigate to="/home" replace />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-[hsl(var(--foreground-muted))]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === 'pending') return <Navigate to="/pending" replace />;
  if (user.force_password_change) return <Navigate to="/change-password" replace />;
  return <Navigate to="/home" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <PrefsProvider>
        <FeatureProvider>
        <BrowserRouter>
          <GuidedTourProvider>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-otp" element={<VerifyOTPPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<Protected><ChangePasswordPage /></Protected>} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/pending" element={<PendingPage />} />
            <Route path="/wrong-domain" element={<WrongDomainPage />} />

            <Route path="/home" element={<Protected><HomePage /></Protected>} />
            <Route path="/design-desk" element={<Protected allow={['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture', 'team']}><MyDesignDeskPage /></Protected>} />
            <Route path="/projects" element={<Protected><ProjectsPage /></Protected>} />
            <Route path="/master-projects" element={<Protected><MasterProjectsPage /></Protected>} />
            <Route path="/followups" element={<Protected><FollowupsPage /></Protected>} />
            <Route path="/archives" element={<Protected><ArchivesPage /></Protected>} />
            <Route path="/team" element={<Protected><TeamDirectoryPage /></Protected>} />
            <Route path="/approvals" element={<Protected allow={['owner']}><ApprovalsPage /></Protected>} />
            <Route path="/outbox" element={<Protected allow={['owner', 'operations']}><OutboxPage /></Protected>} />
            <Route path="/guide" element={<Protected><GuidePage /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetailPage /></Protected>} />
            <Route path="/projects/:id/qc-history" element={<Protected><QCHistoryPage /></Protected>} />
            <Route path="/admin/access" element={<Protected allow={['owner']}><AdminAccessPage /></Protected>} />
            <Route path="/admin/wo-review" element={<Protected allow={['owner']}><WOReviewPage /></Protected>} />
            <Route path="/work-orders" element={<Protected allow={['owner', 'operations', 'accounts']}><WorkOrdersPage /></Protected>} />
            <Route path="/work-orders/upload" element={<Protected allow={['owner', 'operations']}><WorkOrderUploadPage /></Protected>} />
            <Route path="/work-orders/manual" element={<Protected allow={['owner', 'operations']}><WorkOrderManualPage /></Protected>} />
            <Route path="/work-orders/:id/verify" element={<Protected allow={['owner', 'operations', 'accounts']}><WorkOrderVerifyPage /></Protected>} />
            <Route path="/pending-invoices" element={<Protected allow={['owner', 'accounts', 'commercials']}><PendingInvoicesPage /></Protected>} />
            <Route path="/quotes" element={<Protected allow={['owner', 'accounts', 'commercials', 'operations']}><QuoteRegisterPage /></Protected>} />
            <Route path="/visits" element={<Protected allow={['owner', 'operations', 'accounts', 'commercials', 'dm_creative', 'dm_technical', 'dm_architecture', 'team']}><VisitsPage /></Protected>} />
            <Route path="/kra" element={<Protected><KraDashboardPage /></Protected>} />
            <Route path="/timesheets" element={<Protected><TimesheetsPage /></Protected>} />
            <Route path="/commitments" element={<Protected><CommitmentsPage /></Protected>} />
            <Route path="/my-week" element={<Protected><MyWeekPage /></Protected>} />
            <Route path="/admin/holidays" element={<Protected allow={['owner']}><AdminHolidayCalendarPage /></Protected>} />
            <Route path="/admin/wor-archive" element={<Protected allow={['owner', 'operations', 'hr']}><AdminWorArchivePage /></Protected>} />
            <Route path="/admin/gate-delegations" element={<Protected allow={['owner', 'operations']}><AdminGateDelegationsPage /></Protected>} />
            <Route path="/admin/sop-needs-confirmation" element={<Protected allow={['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture']}><AdminSopNeedsConfirmationPage /></Protected>} />
            <Route path="/admin/settings" element={<Protected allow={['owner']}><AdminSettingsPage /></Protected>} />
            <Route path="/admin/drive-setup" element={<Protected allow={['owner']}><DriveSetupWizardPage /></Protected>} />
            <Route path="/admin/release-manager" element={<Protected allow={['owner']}><ReleaseManagerPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsKraPage /></Protected>} />
            <Route path="/reports/builder" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/reports/data-quality" element={<Protected><DataQualityPage /></Protected>} />
            <Route path="/my-work" element={<Protected><MyWorkPage /></Protected>} />
            <Route path="/knowledge" element={<Protected><KnowledgeHubPage /></Protected>} />
            <Route path="/knowledge-old" element={<Protected><KnowledgePage /></Protected>} />
            <Route path="/knowledge/:pageId" element={<Protected><KnowledgePage /></Protected>} />
            <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
            <Route path="/emails" element={<Protected><EmailsPage /></Protected>} />
            <Route path="/wizard" element={<Protected><WizardPage /></Protected>} />
            <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/admin/users" element={<Protected allow={['owner']}><AdminUsersPage /></Protected>} />
            <Route path="/admin/audit" element={<Protected allow={['owner', 'operations']}><AdminAuditPage /></Protected>} />
            <Route path="/admin/feedback" element={<Protected allow={['owner']}><AdminFeedbackPage /></Protected>} />

            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
          </GuidedTourProvider>
        </BrowserRouter>
        </FeatureProvider>
      </PrefsProvider>
    </AuthProvider>
  );
}
